<template>
  <div>
    <div class="order-summary">
      <h2>Your Order</h2>

      <div v-if="pricingAvailabilityChanged" class="info-message error-message">
        <div>Items in your cart have changed pricing or availability</div>
        <button @click="updateCartPricingAvailability" class="small-button">Update</button>
      </div>

      <table>
        <tr>
          <th class="upper">Product</th>
          <th class="upper">Qty</th>
          <th class="upper">Amt</th>
        </tr>
          <tr><td colspan='3'>
            <transition name='error-fade'>
              <div v-if="errorMessage" class="info-message error-message">{{ errorMessage }}</div>
            </transition>
          </td></tr>
        <tr v-for="bulkItem in bulkItems" :key="bulkItem.id">
          <td>{{ bulkItem.name }}</td>
          <td style="min-width: 110px">
            <input
              class="quantity-field"
              type="number"
              oninput="this.value = Math.abs(this.value)"
              v-model="bulkItem.quantity"
              :disabled="!editable"
              @change="updateNavQty"
              @input="addToStore(bulkItem, 'bulkProductLineItems')"
              @blur="resetBulk(bulkItem)"
            >
            <a v-if="editable && soilEditable" class="remove-item" @click="removeLineItem(bulkItem,'bulkProductLineItems')">
              <i class="fa fa-trash" />
            </a>
          </td>
          <td>{{ formatMoney(bulkItem.quantity * bulkItem.base_price) }}</td>
        </tr>
        <tr v-for="otherItem in otherItems" :key="otherItem.id">
          <td>{{ otherItem.name }}</td>
          <td style="min-width: 110px">
            <input
              class="quantity-field"
              type="number"
              oninput="this.value = Math.abs(this.value)"
              v-model="otherItem.quantity"
              :disabled="!editable"
              @change="updateNavQty"
              @input="addToStore(otherItem, 'otherProductLineItems')"
              @blur="resetOther(otherItem)"
            >
            <a v-if="editable" class="remove-item" @click="removeLineItem(otherItem,'otherProductLineItems')">
              <i class="fa fa-trash" />
            </a>
          </td>
          <td>{{ formatMoney(otherItem.quantity * otherItem.base_price) }}</td>
        </tr>
      </table>

      <hr>

      <div class="subtotal-section">
        <table>
          <tr>
            <th>Subtotal:</th>
            <td>${{ subTotal }}</td>
          </tr>
          <tr>
            <th>Discount:</th>
            <td>(${{ discount }})</td>
          </tr>
          <tr v-if="this.$store.state.wincode.discount > 0">
            <th>{{`${this.$store.state.wincode.name}:`}}</th>
            <td>(${{ discountCodeAmount }})</td>
          </tr>
          <tr>
            <th>Out of Delivery Zone Fee:</th>
            <td>${{ deliveryFee }}</td>
          </tr>
          <tr v-for="tax of orderTaxes" :key="tax.id">
            <th>{{ `${tax.name} ${formatMoney(tax.rate * 100)}% : ` }}</th>
            <td>${{ formatMoney(tax.total) }}</td>
          </tr>
        </table>
      </div>
    </div>

    <div class="total-section">
      <span class="total-text">Total</span> (Taxes Incl.)
      <span class="total">${{ total }}</span>
    </div>
  </div>
</template>

<script>
export default {
  name: 'OrderSummary',

  data() {
    return {
      errorMessage: false,
      bulkItems: [],
      otherItems: [],
    };
  },

  props: {
    soilEditable: {
      default: true,
      type: Boolean,
    },
    editable: {
      default: true,
      type: Boolean,
    },
  },

  created() {
    this.bulkItems = this.duplicateObjectHash(this.storeBulkItems);
    this.otherItems = this.duplicateObjectHash(this.storeOtherItems);
  },

  computed: {

    storeBulkItems() {
      return this.$store.getters.bulkItemArray;
    },

    storeOtherItems() {
      return this.$store.getters.otherItemArray;
    },

    subTotal() {
      return this.formatMoney(this.$store.getters.subTotal);
    },

    taxableSubtotal() {
      return this.formatMoney(this.$store.getters.taxableSubtotal);
    },

    orderTaxes() {
      return this.$store.getters.orderTaxes;
    },

    discount() {
      return this.formatMoney(this.$store.getters.bagDiscounts);
    },

    discountCodeAmount() {
      return this.formatMoney(this.$store.state.wincode.discount);
    },

    deliveryFee() {
      return this.formatMoney(this.$store.state.delivery_charge);
    },

    total() {
      const totalTaxes = this.orderTaxes.reduce((total, item) => (total + this.numberParser(item.total)), 0);
      const total = (this.numberParser(this.subTotal) - this.numberParser(this.discount) - this.numberParser(this.discountCodeAmount)
        + this.numberParser(this.deliveryFee) + this.numberParser(totalTaxes));

      return this.formatMoney(total);
    },

    pricingAvailabilityChanged() {
      return this.$store.getters.pricingAvailabilityChanged;
    },
  },

  watch: {
    storeBulkItems(newValue) {
      // Duplicated to remove direct link to store attribute
      this.bulkItems = this.duplicateObjectHash(newValue);
    },

    storeOtherItems(newValue) {
      // Duplicated to remove direct link to store attribute
      this.otherItems = this.duplicateObjectHash(newValue);
    },
  },

  methods: {
    addToStore(item, itemType) {
      if (item.quantity > 0) {
        this.$store.dispatch('addLineItem', { item, itemType });
      }
    },

    updateCartPricingAvailability() {
      this.$store.dispatch('updateCartPricingAvailability');
    },

    resetBulk(item) {
      const { quantity } = item;

      if (quantity < 1 && this.soilEditable) {
        this.removeLineItem(item, 'bulkProductLineItems');
      } else {
        const errorMessage = (quantity < 1) ? 'Orders must have at least 1 soil product' : null;
        this.setErrorMessage(errorMessage);
        this.bulkItems = this.duplicateObjectHash(this.storeBulkItems);
      }
    },

    resetOther(item) {
      const { quantity } = item;

      if (quantity < 1 && this.editable) {
        this.removeLineItem(item, 'otherProductLineItems');
      } else {
        this.otherItems = this.duplicateObjectHash(this.storeOtherItems);
      }
    },

    duplicateObjectHash(objectHash) {
      return Object.keys(objectHash).map((key) => { return { ...objectHash[key] }; });
    },

    setErrorMessage(message) {
      this.errorMessage = message;
      setTimeout(() => { this.errorMessage = false; }, 2000);
    },

    formatMoney(money) {
      return accounting.format(money, { precision: 2 });
    },

    numberParser(num) {
      const moneyNumber = accounting.format(num, { precision: 2 });
      return parseFloat(moneyNumber.replace(/\,/g,''), 10);
    },

    removeLineItem(item, type) {
      const data = {
        itemType: type,
        item,
      };
      this.$store.dispatch('removeLineItem', data);
    },

    /**
     * Updates quantity in Navigation menu cart icon
     */
    updateNavQty() {
      this.$store.dispatch('updateNavQty');
    },
  },
};
</script>

<style scoped>
.quantity-field {
  border-radius: .38em;
  -webkit-appearance: none;
  appearance: none;
  color: inherit;
  background-color: transparent;
  border: 2px solid #009245;
  text-align: center;
  padding: .24em;
  max-width: 2.62em;
  margin: .24em .38em;
}

.quantity-field:disabled {
  border: none;
  margin: 0;
  padding: 0;
}

.remove-item {
  margin-left: .62em;
  cursor: pointer;
}

.remove-item:hover {
  color: black;
}

.order-summary ul li .summary-item {
  flex: 1;
}

.fa {
  display: inline;
}

table {
  width: 100%;
}

table th {
  text-align: left;
  width: 100%;
}

table th.upper {
  text-transform: uppercase;
}

table th:last-of-type,
table td:last-of-type {
  text-align: right;
}

/* Vue transition classes - used with transition name: fade */
.error-fade-leave-active {
    transition: opacity .5s;
}

.error-fade-leave-to /* .fade-leave-active in <2.1.8 */ {
    opacity: 0;
}

@media (max-width: 480px) {
  .order-summary ul li {
    flex-direction: column;
    align-items: flex-start;
  }

  .order-summary ul li .price {
    width: 100%;
  }
}
</style>
